import type { CSSProperties } from 'react';
import type { PreviewBreakpoint, SectionStyle, SectionStylePatch } from './section-templates';
import { resolveSectionStyle } from './section-templates';
import { safeBackgroundImage } from './safe-url';

const WIDTHS: Record<string, string> = {
  full: 'w-full px-4',
  wide: 'mx-auto w-full max-w-[1400px] px-4',
  normal: 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8',
  narrow: 'mx-auto w-full max-w-3xl px-4',
};

export function containerClass(
  style?: SectionStyle,
  breakpoint: PreviewBreakpoint = 'desktop',
): string {
  const resolved = resolveSectionStyle(style, breakpoint);
  return WIDTHS[resolved.containerWidth ?? 'normal'];
}

export function sectionStyle(
  style?: SectionStyle,
  breakpoint: PreviewBreakpoint = 'desktop',
): CSSProperties {
  const resolved = resolveSectionStyle(style, breakpoint);
  if (resolved.hidden) {
    return { display: 'none' };
  }
  const css: CSSProperties = {
    paddingTop: resolved.paddingTop,
    paddingBottom: resolved.paddingBottom,
  };
  if (resolved.textAlign) {
    css.textAlign = resolved.textAlign;
  }
  if (resolved.headingColor && /^#[0-9a-fA-F]{6}$/.test(resolved.headingColor)) {
    (css as Record<string, string>)['--section-heading'] = resolved.headingColor;
  }
  if (resolved.bodyColor && /^#[0-9a-fA-F]{6}$/.test(resolved.bodyColor)) {
    (css as Record<string, string>)['--section-body'] = resolved.bodyColor;
  }
  if (resolved.backgroundType === 'color' && resolved.backgroundColor && /^#[0-9a-fA-F]{6}$/.test(resolved.backgroundColor)) {
    css.backgroundColor = resolved.backgroundColor;
  }
  if (resolved.backgroundType === 'gradient' && resolved.backgroundGradient) {
    const gradient = safeBackgroundImage(resolved.backgroundGradient, 'gradient');
    if (gradient) css.backgroundImage = gradient;
  }
  if (resolved.backgroundType === 'image' && resolved.backgroundImage) {
    const image = safeBackgroundImage(resolved.backgroundImage, 'image');
    if (image) {
      css.backgroundImage = image;
      css.backgroundSize = 'cover';
      css.backgroundPosition = 'center';
    }
  }
  return css;
}

function patchCssRules(selector: string, patch: SectionStylePatch): string {
  const rules: string[] = [];
  if (typeof patch.paddingTop === 'number') rules.push(`padding-top:${patch.paddingTop}px`);
  if (typeof patch.paddingBottom === 'number') rules.push(`padding-bottom:${patch.paddingBottom}px`);
  if (patch.hidden === true) rules.push('display:none');
  if (patch.hidden === false) rules.push('display:block');
  if (patch.textAlign) rules.push(`text-align:${patch.textAlign}`);
  if (!rules.length) return '';
  return `${selector}{${rules.join(';')}}`;
}

/** Emits tablet/mobile override CSS for the published page (independent of editor preview). */
export function sectionResponsiveCss(sectionId: string, style?: SectionStyle): string {
  if (!style) return '';
  const safeId = sectionId.replace(/[^a-zA-Z0-9_-]/g, '');
  const selector = `[data-section-id="${safeId}"]`;
  const parts: string[] = [];
  if (style.tablet && Object.keys(style.tablet).length > 0) {
    const rules = patchCssRules(selector, style.tablet);
    if (rules) parts.push(`@media (max-width:1024px){${rules}}`);
  }
  if (style.mobile && Object.keys(style.mobile).length > 0) {
    const rules = patchCssRules(selector, style.mobile);
    if (rules) parts.push(`@media (max-width:640px){${rules}}`);
  }
  return parts.join('');
}
